import { useEffect, useMemo, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { toast } from "react-toastify";
import api from "@/utils/axios";
import { connectSocket, getSocket } from "@/utils/socket";
import { useAuth } from "@/app/auth-context-state";
import { CallCtx } from "@/app/call-context-state";

const AGORA_APP_ID = import.meta.env.VITE_AGORA_APP_ID || "";

const emptyTracks = { audio: null, video: null };

export function CallProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || user?._id || null;
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState("idle");
  const [isConnecting, setIsConnecting] = useState(false);
  const [localTracks, setLocalTracks] = useState(emptyTracks);
  const [remoteTracks, setRemoteTracks] = useState(emptyTracks);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [activeCameraId, setActiveCameraId] = useState(null);

  const clientRef = useRef(null);
  const localTrackRef = useRef(emptyTracks);

  const cleanupAgora = async () => {
    const client = clientRef.current;
    if (client) {
      client.removeAllListeners();
      await client.leave();
    }
    clientRef.current = null;

    if (localTrackRef.current.audio) {
      localTrackRef.current.audio.stop();
      localTrackRef.current.audio.close();
    }
    if (localTrackRef.current.video) {
      localTrackRef.current.video.stop();
      localTrackRef.current.video.close();
    }

    localTrackRef.current = emptyTracks;
    setLocalTracks(emptyTracks);
    setRemoteTracks(emptyTracks);
    setIsMuted(false);
    setIsCameraOff(false);
  };

  const attachAgoraListeners = (client) => {
    client.on("user-published", async (user, mediaType) => {
      await client.subscribe(user, mediaType);

      if (mediaType === "video") {
        setRemoteTracks((prev) => ({ ...prev, video: user.videoTrack }));
      }

      if (mediaType === "audio") {
        user.audioTrack?.play();
        setRemoteTracks((prev) => ({ ...prev, audio: user.audioTrack }));
      }
    });

    client.on("user-unpublished", (_user, mediaType) => {
      if (mediaType === "video") {
        setRemoteTracks((prev) => ({ ...prev, video: null }));
      }
      if (mediaType === "audio") {
        setRemoteTracks((prev) => ({ ...prev, audio: null }));
      }
    });

    client.on("user-left", () => {
      setRemoteTracks(emptyTracks);
    });
  };

  const joinAgora = async ({ channelName, token, callType, appId, uid }) => {
    const resolvedAppId = appId || AGORA_APP_ID;
    if (!resolvedAppId) {
      toast.error("Missing Agora App ID");
      throw new Error("Missing Agora App ID");
    }

    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;
    attachAgoraListeners(client);

    const resolvedUid = uid || userId;
    await client.join(resolvedAppId, channelName, token, resolvedUid);

    let audioTrack = null;
    let videoTrack = null;

    if (callType === "video") {
      [audioTrack, videoTrack] =
        await AgoraRTC.createMicrophoneAndCameraTracks();
    } else {
      audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    }

    localTrackRef.current = { audio: audioTrack, video: videoTrack };
    setLocalTracks({ audio: audioTrack, video: videoTrack });
    const nextCameraId = videoTrack?.getTrackSettings?.()?.deviceId || null;
    setActiveCameraId(nextCameraId);

    const publishTracks = [audioTrack, videoTrack].filter(Boolean);
    if (publishTracks.length) {
      await client.publish(publishTracks);
    }
  };

  const startCall = async ({ calleeId, callType }) => {
    if (isConnecting) return;
    try {
      setIsConnecting(true);
      const { data } = await api.post("/v1/calls", { calleeId, callType });
      const call = data?.call;
      const token = data?.token;
      const appId = data?.appId;
      if (!call || !token) throw new Error("Missing call data");

      setActiveCall(call);
      setCallStatus(call.status || "ringing");
      await joinAgora({
        channelName: call.channelName,
        token,
        callType: call.callType,
        appId,
        uid: data?.agoraUid,
      });
    } catch (err) {
      console.error("Failed to start call", err);
      toast.error(err?.response?.data?.message || "Could not start call");
      await cleanupAgora();
      setActiveCall(null);
      setCallStatus("idle");
    } finally {
      setIsConnecting(false);
    }
  };

  const answerCall = async () => {
    if (!incomingCall || isConnecting) return;
    try {
      setIsConnecting(true);
      const { data } = await api.post(
        `/v1/calls/${incomingCall.call.id}/answer`,
      );
      const call = data?.call;
      const token = data?.token;
      const appId = data?.appId;
      if (!call || !token) throw new Error("Missing call data");

      setIncomingCall(null);
      setActiveCall(call);
      setCallStatus(call.status || "accepted");

      await joinAgora({
        channelName: call.channelName,
        token,
        callType: call.callType,
        appId,
        uid: data?.agoraUid,
      });
    } catch (err) {
      console.error("Failed to answer call", err);
      toast.error(err?.response?.data?.message || "Could not answer call");
    } finally {
      setIsConnecting(false);
    }
  };

  const declineCall = async () => {
    if (!incomingCall) return;
    try {
      await api.post(`/v1/calls/${incomingCall.call.id}/end`);
    } catch (err) {
      console.error("Failed to decline call", err);
    } finally {
      setIncomingCall(null);
    }
  };

  const endCall = async () => {
    if (!activeCall) return;
    try {
      await api.post(`/v1/calls/${activeCall.id}/end`);
    } catch (err) {
      console.error("Failed to end call", err);
    } finally {
      await cleanupAgora();
      setActiveCall(null);
      setCallStatus("idle");
    }
  };

  const toggleMute = async () => {
    const audioTrack = localTrackRef.current.audio;
    if (!audioTrack) return;
    const next = !isMuted;
    await audioTrack.setEnabled(!next);
    setIsMuted(next);
  };

  const toggleCamera = async () => {
    const videoTrack = localTrackRef.current.video;
    if (!videoTrack) return;
    const next = !isCameraOff;
    await videoTrack.setEnabled(!next);
    setIsCameraOff(next);
  };

  const switchCamera = async () => {
    const videoTrack = localTrackRef.current.video;
    if (!videoTrack) return;
    const cameras = await AgoraRTC.getCameras();
    if (cameras.length < 2) return;

    const currentId =
      activeCameraId || videoTrack.getTrackSettings?.()?.deviceId || null;
    const currentIndex = cameras.findIndex(
      (camera) => camera.deviceId === currentId,
    );
    const nextIndex =
      currentIndex >= 0 ? (currentIndex + 1) % cameras.length : 0;
    const nextCamera = cameras[nextIndex];
    if (!nextCamera?.deviceId || nextCamera.deviceId === currentId) return;

    await videoTrack.setDevice(nextCamera.deviceId);
    setActiveCameraId(nextCamera.deviceId);
  };

  useEffect(() => {
    if (!userId) return;
    const accessToken = localStorage.getItem("cm_at");
    if (!accessToken) return;

    const socket = connectSocket(accessToken);

    const handleIncoming = (payload) => {
      setIncomingCall(payload);
    };

    const handleAccepted = (payload) => {
      if (!payload?.call) return;
      setActiveCall(payload.call);
      setCallStatus(payload.call.status || "accepted");
    };

    const handleEnded = () => {
      cleanupAgora().finally(() => {
        setActiveCall(null);
        setIncomingCall(null);
        setCallStatus("idle");
      });
    };

    socket.on("call:incoming", handleIncoming);
    socket.on("call:accepted", handleAccepted);
    socket.on("call:ended", handleEnded);

    return () => {
      const currentSocket = getSocket();
      if (!currentSocket) return;
      currentSocket.off("call:incoming", handleIncoming);
      currentSocket.off("call:accepted", handleAccepted);
      currentSocket.off("call:ended", handleEnded);
    };
  }, [userId]);

  const value = useMemo(
    () => ({
      incomingCall,
      activeCall,
      callStatus,
      isConnecting,
      localTracks,
      remoteTracks,
      isMuted,
      isCameraOff,
      startCall,
      answerCall,
      declineCall,
      endCall,
      toggleMute,
      toggleCamera,
      switchCamera,
    }),
    [
      incomingCall,
      activeCall,
      callStatus,
      isConnecting,
      localTracks,
      remoteTracks,
      isMuted,
      isCameraOff,
      activeCameraId,
    ],
  );

  return <CallCtx.Provider value={value}>{children}</CallCtx.Provider>;
}
